import { useState, useCallback } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../../Keycloak/useAuthenticatedFetchWithWorkspace";

interface CreateEntityTimeslotParams {
    entityId: number;
    channelId: number;
    startHour: string;
    endHour: string;
}

interface UpdateEntityTimeslotParams {
    timeslotId: number;
    startHour: string;
    endHour: string;
}

interface EntityTimeslotResource {
    id: number;
    [key: string]: unknown;
}

const useEntityTimeslotMutations = () => {
    const { authFetch } = useAuthenticatedFetchWithWorkspace();
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>("");

    const createEntityTimeslot = useCallback(async (params: CreateEntityTimeslotParams): Promise<EntityTimeslotResource | null> => {
        setLoading(true);
        setError("");
        try {
            const result: EntityTimeslotResource = await authFetch("/ent_entity_timeslots", {
                method: "POST",
                body: JSON.stringify({
                    channelId: params.channelId,
                    startHour: params.startHour,
                    endHour: params.endHour,
                    entity: `/api/ent_entities/${params.entityId}`,
                }),
            });
            return result;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "An unknown error occurred");
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    const updateEntityTimeslot = useCallback(async (params: UpdateEntityTimeslotParams): Promise<EntityTimeslotResource | null> => {
        setLoading(true);
        setError("");
        try {
            const result: EntityTimeslotResource = await authFetch(`/ent_entity_timeslots/${params.timeslotId}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/merge-patch+json",
                } as HeadersInit,
                body: JSON.stringify({
                    startHour: params.startHour,
                    endHour: params.endHour,
                }),
            });
            return result;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "An unknown error occurred");
            return null;
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    const deleteEntityTimeslot = useCallback(async (timeslotId: number): Promise<boolean> => {
        setLoading(true);
        setError("");
        try {
            await authFetch(`/ent_entity_timeslots/${timeslotId}`, {
                method: "DELETE",
            });
            return true;
        } catch (err: unknown) {
            setError(err instanceof Error ? err.message : "An unknown error occurred");
            return false;
        } finally {
            setLoading(false);
        }
    }, [authFetch]);

    return { createEntityTimeslot, updateEntityTimeslot, deleteEntityTimeslot, loading, error };
};

export default useEntityTimeslotMutations;
